<template>
  <div class="bg-white rounded-lg border-2 border-gray-900 p-6">
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-xs text-gray-500 uppercase tracking-wider font-medium">
        AI Insights
      </h3>
      <button
        @click="generateInsights"
        :disabled="generating"
        class="text-xs text-gray-400 hover:text-gray-600 flex items-center disabled:opacity-50">
        <svg
          :class="['w-4 h-4 mr-1', generating ? 'animate-spin' : '']"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        {{ generating ? 'Generating...' : 'Refresh' }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="space-y-3">
      <div v-for="n in 2" :key="n" class="animate-pulse">
        <div class="h-4 bg-gray-200 rounded w-3/4 mb-2" />
        <div class="h-3 bg-gray-200 rounded w-full" />
      </div>
    </div>

    <!-- Insights List -->
    <div v-else-if="insights.length > 0" class="space-y-4">
      <div
        v-for="insight in displayedInsights"
        :key="insight.id"
        class="p-3 bg-gray-50 rounded-lg">
        <!-- Type Badge & Confidence -->
        <div class="flex items-center justify-between mb-2">
          <span :class="['text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded', getTypeBadgeClass(insight.type)]">
            {{ formatType(insight.type) }}
          </span>
          <div class="flex items-center">
            <div class="flex space-x-0.5 mr-2">
              <div
                v-for="n in 5"
                :key="n"
                :class="[
                  'w-1 h-2 rounded-sm',
                  n <= getConfidenceLevel(insight.confidence) ? 'bg-gray-900' : 'bg-gray-200'
                ]" />
            </div>
            <button
              @click="shareInsight(insight)"
              class="text-gray-400 hover:text-gray-600">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
              </svg>
            </button>
          </div>
        </div>

        <!-- Title & Content -->
        <h4 class="text-sm font-medium text-gray-900 mb-1">
          {{ insight.title }}
        </h4>
        <p class="text-xs text-gray-600 leading-relaxed">
          {{ insight.content }}
        </p>
      </div>

      <!-- Show More -->
      <button
        v-if="insights.length > 3 && !showAll"
        @click="showAll = true"
        class="w-full text-center text-xs text-gray-500 hover:text-gray-700 py-2">
        Show {{ insights.length - 3 }} more insights
      </button>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-6">
      <svg class="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
      <p class="text-sm text-gray-500 mb-2">
        No insights yet
      </p>
      <p class="text-xs text-gray-400">
        Log more wellness data to receive AI-powered insights
      </p>
    </div>

    <!-- Medical Disclaimer -->
    <div class="mt-4 pt-3 border-t border-gray-100">
      <p class="text-[10px] text-gray-400 text-center">
        AI insights are for informational purposes only, not medical advice.
      </p>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InsightsCard',

  props: {
    insights: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  emits: ['generate', 'share'],

  data() {
    return {
      generating: false,
      showAll: false
    }
  },

  computed: {
    displayedInsights() {
      return this.showAll ? this.insights : this.insights.slice(0, 3)
    }
  },

  methods: {
    async generateInsights() {
      if (this.generating) return
      this.generating = true
      this.$emit('generate')

      // Reset after timeout (parent should update loading prop)
      setTimeout(() => {
        this.generating = false
      }, 30000)
    },

    shareInsight(insight) {
      this.$emit('share', insight)
    },

    formatType(type) {
      const labels = {
        daily_summary: 'Daily',
        weekly_summary: 'Weekly',
        pattern: 'Pattern',
        correlation: 'Correlation',
        recommendation: 'Tip',
        anomaly: 'Alert'
      }
      return labels[type] || type
    },

    getTypeBadgeClass(type) {
      const classes = {
        daily_summary: 'bg-blue-100 text-blue-700',
        weekly_summary: 'bg-purple-100 text-purple-700',
        pattern: 'bg-green-100 text-green-700',
        correlation: 'bg-yellow-100 text-yellow-700',
        recommendation: 'bg-indigo-100 text-indigo-700',
        anomaly: 'bg-red-100 text-red-700'
      }
      return classes[type] || 'bg-gray-100 text-gray-700'
    },

    getConfidenceLevel(confidence) {
      if (!confidence) return 0
      const percent = parseFloat(confidence) * 100
      if (percent >= 90) return 5
      if (percent >= 75) return 4
      if (percent >= 60) return 3
      if (percent >= 40) return 2
      return 1
    }
  }
}
</script>
